import api from "./api";

export interface LoginCredentials {
  email: string;
  password: string;
  recaptchaToken?: string | null;
}

export interface AuthUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: "ADMIN" | "DEALER" | "MEMBER" | "GUEST";
  disabled: boolean;
  city: string | null;
  country: string | null;
  language: string | null;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  user: AuthUser;
}

interface RefreshTokenRequest {
  refreshToken: string;
  userId: number;
}

interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

/**
 * Login user with email and password
 */
const login = async (
  credentials: LoginCredentials,
): Promise<AuthResponse> => {
  const { email, password, recaptchaToken } = credentials;
  const loginPayload = {
    email,
    password,
    token: recaptchaToken || "",
  };

  const response = await api.post<AuthResponse>(
    "dpp/login",
    // "http://localhost:3000/lgportal-uat/login ",
    loginPayload,
  );

  if (response.data.token) {
    // Store tokens in localStorage
    localStorage.setItem("auth_token", response.data.token);
    localStorage.setItem("refresh_token", response.data.refreshToken);
    localStorage.setItem("user_data", JSON.stringify(response.data.user));

    // Set the token in the default authorization header for all future requests
    api.defaults.headers.common["Authorization"] = `${response.data.token}`;
  }

  return response.data;
};

/**
 * Refresh auth token using the refresh token
 */
export const refreshAuthToken = async (): Promise<AuthResponse> => {
  const refreshToken = localStorage.getItem("refresh_token");
  const userData = getCurrentUser();

  if (!refreshToken || !userData) {
    throw new Error("No refresh token available");
  }

  const refreshData: RefreshTokenRequest = {
    refreshToken,
    userId: userData.id,
  };

  try {
    const response = await api.post<AuthResponse>(
      "dpp/auth/refresh-token",
      // "http://localhost:3000/lgportal-uat/auth/refresh-token",
      refreshData,
      {
        headers: {
          Authorization: undefined, // Ensure no auth header is sent for this request
        },
      },
    );

    if (response.data.token) {
      // Update tokens in localStorage
      localStorage.setItem("auth_token", response.data.token);
      localStorage.setItem("refresh_token", response.data.refreshToken);
      localStorage.setItem("user_data", JSON.stringify(response.data.user));

      // Update the authorization header
      api.defaults.headers.common["Authorization"] = `${response.data.token}`;
    }

    return response.data;
  } catch (error) {
    // If refresh fails, clear auth data and force re-login
    logout();
    throw error;
  }
};

/**
 * Logout user and clear stored data
 */
export const logout = (): void => {
  // Remove tokens from localStorage
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_data");

  // Clear session storage
  sessionStorage.clear();

  // Clear cookies related to authentication
  document.cookie.split(";").forEach((cookie) => {
    const trimmedCookie = cookie.trim();
    const cookieName = trimmedCookie.split("=")[0];
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

  // Remove the authorization header
  delete api.defaults.headers.common["Authorization"];

  console.log("User authentication data and cache cleared");
};

/**
 * Get current authenticated user from storage
 */
export const getCurrentUser = (): AuthUser | null => {
  const userData = localStorage.getItem("user_data");
  if (userData) {
    return JSON.parse(userData);
  }
  return null;
};

/**
 * Merge updated fields into the stored auth user (e.g. after profile PATCH)
 */
export const updateStoredUser = (
  partial: Partial<AuthUser>,
): AuthUser | null => {
  const current = getCurrentUser();
  if (!current) return null;
  const updated = { ...current, ...partial };
  localStorage.setItem("user_data", JSON.stringify(updated));
  return updated;
};

/**
 * Check if user is authenticated
 */
const isAuthenticated = (): boolean => {
  return !!localStorage.getItem("auth_token");
};

/**
 * Initialize the auth token from localStorage if it exists
 */
const initializeAuthHeaders = (): void => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    api.defaults.headers.common["Authorization"] = `${token}`;
  }
};

// Initialize headers on module load
initializeAuthHeaders();

/**
 * Request password reset for a given email
 */
export const forgotPassword = async (
  email: string,
): Promise<ForgotPasswordResponse> => {
  try {
    const response = await api.post<ForgotPasswordResponse>(
      "dpp/user/forgot-password",
      { email },
    );
    return response.data;
  } catch (error: any) {
    // Handle specific error cases
    if (error.response?.status === 404) {
      // We don't want to reveal if an email exists in our system for security reasons
      // So we return a generic success message even when the email doesn't exist
      return {
        success: true,
        message:
          "If an account with that email exists, a password reset link has been sent.",
      };
    }

    // For other errors, throw the error for the component to handle
    throw error;
  }
};

/**
 * Reset password with PIN verification (portal users)
 */
export const resetPassword = async ({
  token,
  newPassword,
}: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  try {
    const response = await api.post<ResetPasswordResponse>(
      "dpp/auth/reset-password",
      {
        token,
        newPassword,
      },
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const resetAppUserPassword = async ({
  token,
  newPassword,
}: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  try {
    const response = await api.post<ResetPasswordResponse>(
      "dpp/user/reset-password",
      {
        token,
        newPassword,
      },
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

const authService = {
  login,
  logout,
  getCurrentUser,
  updateStoredUser,
  isAuthenticated,
  refreshAuthToken,
  forgotPassword,
  resetPassword,
  resetAppUserPassword,
};

export default authService;
